import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Card, Input, Button, message, Modal, Avatar, Tag, Space, Typography, Checkbox, Empty, Spin, List, Image, Pagination, Tabs } from 'antd';
import { PlusOutlined, SendOutlined, UserOutlined, DeleteOutlined, SearchOutlined, VideoCameraOutlined, EyeOutlined, SwapOutlined } from '@ant-design/icons';
import { getEmployeeList, BotEmployee } from '../services/botEmployeeApi';
import { sendEmployeeChat, getEmployeeChatTaskStatus, TaskDetail } from '../services/employeeChatApi';
import { fetchActiveLiveRooms, LiveRoom } from '../services/liveRoomApi';

const { Title, Text } = Typography;

interface MessageInput {
  id: string;
  content: string;
  status: 'editing' | 'pending' | 'sent' | 'failed';
  errorMsg?: string | null;
}

interface SelectedBot {
  botId: number;
  userName: string | null;
  avatar: string | null;
  messages: MessageInput[];
}

const PAGE_SIZE = 50;

interface ChatPanelProps {
  onLabelChange?: (label: string) => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ onLabelChange }) => {
  const [selectedRoom, setSelectedRoom] = useState<LiveRoom | null>(null);
  const [selectedBots, setSelectedBots] = useState<SelectedBot[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [employees, setEmployees] = useState<BotEmployee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [checkedBotIds, setCheckedBotIds] = useState<Set<number>>(new Set());
  const [sending, setSending] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // 直播间选择器
  const [roomPickerOpen, setRoomPickerOpen] = useState(false);
  const [rooms, setRooms] = useState<LiveRoom[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [roomTotal, setRoomTotal] = useState(0);
  const [roomPage, setRoomPage] = useState(1);
  const [roomKeyword, setRoomKeyword] = useState('');

  // 清理轮询
  useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  // 选中房间变化时同步 Tab 标签
  useEffect(() => {
    if (!onLabelChange) return;
    const label = selectedRoom
      ? (selectedRoom.roomName?.trim() || `房间 ${selectedRoom.roomId}`).slice(0, 12)
      : '';
    onLabelChange(label);
  }, [selectedRoom, onLabelChange]);

  const loadRooms = useCallback(async (page: number, search?: string) => {
    setLoadingRooms(true);
    try {
      const data = await fetchActiveLiveRooms(page, PAGE_SIZE, search);
      setRooms(data.list || []);
      setRoomTotal(data.totalCount || 0);
      setRoomPage(data.currPage || page);
    } catch (e: any) {
      message.error(e.message || '加载直播间列表失败');
      setRooms([]);
      setRoomTotal(0);
    } finally {
      setLoadingRooms(false);
    }
  }, []);

  const openRoomPicker = useCallback(() => {
    setRoomPickerOpen(true);
    setRoomKeyword('');
    loadRooms(1);
  }, [loadRooms]);

  // 关键字 debounce → 服务端搜索
  useEffect(() => {
    if (!roomPickerOpen) return;
    const handle = setTimeout(() => {
      loadRooms(1, roomKeyword);
    }, 300);
    return () => clearTimeout(handle);
  }, [roomKeyword, roomPickerOpen, loadRooms]);

  const pickRoom = useCallback((room: LiveRoom) => {
    setSelectedRoom(room);
    setRoomPickerOpen(false);
  }, []);

  const openBotModal = useCallback(async () => {
    setModalOpen(true);
    setLoadingEmployees(true);
    try {
      const list = await getEmployeeList();
      setEmployees(list);
      // 预选已选中的
      setCheckedBotIds(new Set(selectedBots.map(b => b.botId)));
    } catch (e: any) {
      message.error(e.message);
    } finally {
      setLoadingEmployees(false);
    }
  }, [selectedBots]);

  const confirmBotSelection = useCallback(() => {
    const existingMap = new Map(selectedBots.map(b => [b.botId, b]));
    const newSelected: SelectedBot[] = [];
    for (const emp of employees) {
      if (checkedBotIds.has(emp.botId)) {
        if (existingMap.has(emp.botId)) {
          newSelected.push(existingMap.get(emp.botId)!);
        } else {
          newSelected.push({
            botId: emp.botId,
            userName: emp.userName,
            avatar: emp.avatar,
            messages: [{ id: crypto.randomUUID(), content: '', status: 'editing' }],
          });
        }
      }
    }
    setSelectedBots(newSelected);
    setModalOpen(false);
  }, [checkedBotIds, employees, selectedBots]);

  const updateMessage = useCallback((botId: number, msgId: string, content: string) => {
    setSelectedBots(prev => prev.map(b =>
      b.botId === botId
        ? { ...b, messages: b.messages.map(m => m.id === msgId ? { ...m, content } : m) }
        : b
    ));
  }, []);

  const addMessage = useCallback((botId: number) => {
    setSelectedBots(prev => prev.map(b =>
      b.botId === botId
        ? { ...b, messages: [...b.messages, { id: crypto.randomUUID(), content: '', status: 'editing' }] }
        : b
    ));
  }, []);

  const removeMessage = useCallback((botId: number, msgId: string) => {
    setSelectedBots(prev => prev.map(b =>
      b.botId === botId
        ? { ...b, messages: b.messages.filter(m => m.id !== msgId) }
        : b
    ));
  }, []);

  const startPolling = useCallback((taskId: string) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const info = await getEmployeeChatTaskStatus(taskId);
        // 按 seq 顺序索引后端明细
        const detailBySeq = new Map<number, TaskDetail>();
        info.details.forEach(d => detailBySeq.set(d.seq, d));

        // 更新消息状态：只匹配本轮 pending 的消息
        setSelectedBots(prev => {
          let seqCounter = 0;
          return prev.map(bot => ({
            ...bot,
            messages: bot.messages.map(m => {
              // 跳过非本轮的消息（editing / 已完成的历史消息）
              if (m.status !== 'pending') return m;
              const detail = detailBySeq.get(seqCounter);
              seqCounter++;
              if (!detail) return m;
              return {
                ...m,
                status: detail.status === 'sent' ? 'sent' as const
                  : detail.status === 'failed' ? 'failed' as const
                  : 'pending' as const,
                errorMsg: detail.errorMsg,
              };
            }),
          }));
        });

        if (info.status === 'completed' || info.status === 'failed') {
          if (pollingRef.current) clearInterval(pollingRef.current);
          pollingRef.current = null;
          setSending(false);
          message.success(`发送完成: 成功 ${info.sentCount}, 失败 ${info.failedCount}`);
        }
      } catch {
        // 忽略轮询错误
      }
    }, 2000);
  }, []);

  // 发送单条消息（"发送此条"按钮）
  const sendSingleMessage = useCallback(async (botId: number, msgId: string) => {
    if (!selectedRoom) { message.warning('请先选择直播间'); return; }
    if (sending) { message.warning('已有发送任务进行中'); return; }

    const bot = selectedBots.find(b => b.botId === botId);
    const msg = bot?.messages.find(m => m.id === msgId);
    if (!msg || msg.status !== 'editing') return;
    const content = msg.content.trim();
    if (!content) { message.warning('消息内容为空'); return; }

    setSending(true);
    setSelectedBots(prev => prev.map(b =>
      b.botId === botId
        ? { ...b, messages: b.messages.map(m => m.id === msgId ? { ...m, status: 'pending' as const } : m) }
        : b
    ));

    try {
      const { taskId } = await sendEmployeeChat(selectedRoom.roomId, [{ botId, content }]);
      startPolling(taskId);
    } catch (e: any) {
      message.error(e.message);
      setSending(false);
      setSelectedBots(prev => prev.map(b =>
        b.botId === botId
          ? { ...b, messages: b.messages.map(m => m.id === msgId ? { ...m, status: 'editing' as const } : m) }
          : b
      ));
    }
  }, [selectedRoom, sending, selectedBots, startPolling]);

  const handleSend = useCallback(async () => {
    if (!selectedRoom) { message.warning('请先选择直播间'); return; }

    // 收集所有 editing 状态的消息
    const msgs: { botId: number; content: string }[] = [];
    let seqCounter = 0;
    const botSeqMap = new Map<number, number[]>();

    for (const bot of selectedBots) {
      const seqs: number[] = [];
      for (const m of bot.messages) {
        if (m.status === 'editing' && m.content.trim()) {
          msgs.push({ botId: bot.botId, content: m.content.trim() });
          seqs.push(seqCounter++);
        }
      }
      if (seqs.length > 0) botSeqMap.set(bot.botId, seqs);
    }

    if (msgs.length === 0) { message.warning('没有可发送的消息'); return; }

    setSending(true);

    // 标记为 pending
    setSelectedBots(prev => prev.map(bot => ({
      ...bot,
      messages: bot.messages.map(m =>
        m.status === 'editing' && m.content.trim() ? { ...m, status: 'pending' as const } : m
      ),
    })));

    try {
      const { taskId } = await sendEmployeeChat(selectedRoom.roomId, msgs);
      startPolling(taskId);
    } catch (e: any) {
      message.error(e.message);
      setSending(false);
      // 回滚状态
      setSelectedBots(prev => prev.map(bot => ({
        ...bot,
        messages: bot.messages.map(m =>
          m.status === 'pending' ? { ...m, status: 'editing' as const } : m
        ),
      })));
    }
  }, [selectedRoom, selectedBots, startPolling]);

  const statusTag = (status: string, errorMsg?: string | null) => {
    switch (status) {
      case 'pending': return <Tag color="blue"><Spin size="small" /> 等待中</Tag>;
      case 'sent': return <Tag color="green">已发送</Tag>;
      case 'failed': return <Tag color="red" title={errorMsg || ''}>失败</Tag>;
      default: return null;
    }
  };

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space align="start" wrap>
          <Text style={{ lineHeight: '32px' }}>直播间:</Text>
          {selectedRoom ? (
            <div
              onClick={openRoomPicker}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1677ff'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(22,119,255,0.08)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8,
                cursor: 'pointer', background: '#fff', minWidth: 380,
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
            >
              {selectedRoom.roomImg ? (
                <Image
                  src={selectedRoom.roomImg}
                  width={44} height={44}
                  style={{ objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                  preview={false}
                  fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0NCIgaGVpZ2h0PSI0NCI+PC9zdmc+"
                />
              ) : (
                <div style={{ width: 44, height: 44, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6, flexShrink: 0 }}>
                  <VideoCameraOutlined style={{ fontSize: 20, color: '#bbb' }} />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Text strong style={{ maxWidth: 200, fontSize: 14 }} ellipsis>
                    {selectedRoom.roomName || `直播间 #${selectedRoom.roomId}`}
                  </Text>
                  <Tag color="red" style={{ margin: 0, fontSize: 11, lineHeight: '16px', padding: '0 6px' }}>直播中</Tag>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#8c8c8c' }}>
                  <Avatar size={16} src={selectedRoom.avatar || undefined} icon={!selectedRoom.avatar ? <UserOutlined /> : undefined} />
                  <span>{selectedRoom.userName || '—'}</span>
                  <span style={{ color: '#d9d9d9' }}>·</span>
                  <span><EyeOutlined style={{ marginRight: 2 }} />{selectedRoom.roomUserCount ?? 0}</span>
                  <span style={{ color: '#d9d9d9' }}>·</span>
                  <span style={{ color: '#bfbfbf' }}>ID {selectedRoom.roomId}</span>
                </div>
              </div>
              <Button size="small" type="text" icon={<SwapOutlined />}
                onClick={(e) => { e.stopPropagation(); openRoomPicker(); }}
                style={{ color: '#1677ff', flexShrink: 0 }}
              >
                更换
              </Button>
            </div>
          ) : (
            <Button icon={<PlusOutlined />} onClick={openRoomPicker}>选择直播间</Button>
          )}
          <Button icon={<PlusOutlined />} onClick={openBotModal}>选择机器人</Button>
        </Space>
      </Card>

      {selectedBots.length === 0 && (
        <Empty description="请先选择机器人" style={{ marginTop: 48 }} />
      )}

      {selectedBots.map(bot => (
        <Card
          key={bot.botId}
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space>
              <Avatar size="small" src={bot.avatar || undefined} icon={!bot.avatar ? <UserOutlined /> : undefined} />
              <Text strong>{bot.userName || `Bot #${bot.botId}`}</Text>
            </Space>
          }
          extra={
            <Button size="small" type="dashed" icon={<PlusOutlined />}
              onClick={() => addMessage(bot.botId)}>
              添加消息
            </Button>
          }
        >
          {bot.messages.map((m, idx) => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Text type="secondary" style={{ minWidth: 24 }}>{idx + 1}.</Text>
              <Input
                style={{ flex: 1 }}
                placeholder="输入消息内容"
                value={m.content}
                disabled={m.status !== 'editing'}
                onChange={e => updateMessage(bot.botId, m.id, e.target.value)}
                onPressEnter={() => {
                  if (idx === bot.messages.length - 1) addMessage(bot.botId);
                }}
              />
              {m.status !== 'editing' && statusTag(m.status, m.errorMsg)}
              {m.status === 'editing' && (
                <Space size={4}>
                  {bot.messages.length > 1 && (
                    <Button size="small" type="text" danger icon={<DeleteOutlined />}
                      onClick={() => removeMessage(bot.botId, m.id)} />
                  )}
                  <Button
                    size="small"
                    type="text"
                    icon={<SendOutlined />}
                    title="发送此条"
                    disabled={!m.content.trim() || sending || !selectedRoom}
                    onClick={() => sendSingleMessage(bot.botId, m.id)}
                    style={{ color: '#1677ff' }}
                  />
                </Space>
              )}
            </div>
          ))}
        </Card>
      ))}

      {selectedBots.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Button type="primary" size="large" icon={<SendOutlined />}
            loading={sending} onClick={handleSend}>
            一键发送
          </Button>
        </div>
      )}

      <Modal
        title="选择直播间"
        open={roomPickerOpen}
        onCancel={() => setRoomPickerOpen(false)}
        footer={null}
        width={680}
      >
        <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
          <Input
            placeholder="主播昵称、房间号、房间名"
            prefix={<SearchOutlined />}
            value={roomKeyword}
            onChange={e => setRoomKeyword(e.target.value)}
            style={{ width: 320 }}
            allowClear
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={() => loadRooms(1, roomKeyword)} loading={loadingRooms}>搜索</Button>
        </Space>
        {loadingRooms ? (
          <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
        ) : rooms.length === 0 ? (
          <Empty description={roomKeyword ? '没有匹配的直播间' : '暂无直播中的房间'} />
        ) : (
          <List
            style={{ maxHeight: 480, overflow: 'auto' }}
            dataSource={rooms}
            renderItem={room => (
              <List.Item
                key={room.roomId}
                onClick={() => pickRoom(room)}
                style={{ cursor: 'pointer', padding: '10px 8px', borderRadius: 4 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <List.Item.Meta
                  avatar={
                    room.roomImg ? (
                      <Image
                        src={room.roomImg}
                        width={56} height={56}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                        preview={false}
                        fallback="data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1NiIgaGVpZ2h0PSI1NiI+PC9zdmc+"
                      />
                    ) : (
                      <div style={{ width: 56, height: 56, background: '#d9d9d9', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4 }}>
                        <VideoCameraOutlined style={{ fontSize: 24, color: '#fff' }} />
                      </div>
                    )
                  }
                  title={
                    <Space>
                      <Text strong>{room.roomName || `直播间 #${room.roomId}`}</Text>
                      <Tag color="red">直播中</Tag>
                      <Tag color="blue"><EyeOutlined /> {room.roomUserCount ?? 0}</Tag>
                    </Space>
                  }
                  description={
                    <Space size={6} style={{ fontSize: 12 }}>
                      <Avatar size={18} src={room.avatar || undefined} icon={!room.avatar ? <UserOutlined /> : undefined} />
                      <span>{room.userName || '—'}</span>
                      {room.handle && <span style={{ color: '#888' }}>@{room.handle}</span>}
                      <span style={{ color: '#888' }}>ID: {room.roomId}</span>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
        {roomTotal > PAGE_SIZE && (
          <div style={{ textAlign: 'right', marginTop: 12 }}>
            <Pagination
              size="small"
              current={roomPage}
              pageSize={PAGE_SIZE}
              total={roomTotal}
              showSizeChanger={false}
              onChange={(p) => loadRooms(p, roomKeyword)}
            />
          </div>
        )}
      </Modal>

      <Modal
        title="选择机器人"
        open={modalOpen}
        onOk={confirmBotSelection}
        onCancel={() => setModalOpen(false)}
        okText="确认"
        cancelText="取消"
      >
        {loadingEmployees ? (
          <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
        ) : employees.length === 0 ? (
          <Empty description="暂无专属员工" />
        ) : (
          <div style={{ maxHeight: 400, overflow: 'auto' }}>
            {employees.map(emp => (
              <div key={emp.botId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                <Checkbox
                  checked={checkedBotIds.has(emp.botId)}
                  onChange={e => {
                    setCheckedBotIds(prev => {
                      const next = new Set(prev);
                      e.target.checked ? next.add(emp.botId) : next.delete(emp.botId);
                      return next;
                    });
                  }}
                />
                <Avatar size="small" src={emp.avatar || undefined} icon={!emp.avatar ? <UserOutlined /> : undefined} />
                <Text>{emp.userName || `Bot #${emp.botId}`}</Text>
                {!emp.isLoggedIn && <Tag color="orange">未登录</Tag>}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

interface ChatTabState {
  id: string;
  customLabel: string; // 来自 ChatPanel 选中房间名；空表示用默认 "会话 N"
  index: number;       // 创建时的序号，用于默认标签 "会话 N"
}

const newTabId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto)
  ? crypto.randomUUID()
  : `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const EmployeeChat: React.FC = () => {
  const initialTab = React.useRef<ChatTabState>({ id: newTabId(), customLabel: '', index: 1 }).current;
  const [tabs, setTabs] = useState<ChatTabState[]>([initialTab]);
  const [activeKey, setActiveKey] = useState<string>(initialTab.id);

  const addTab = useCallback(() => {
    const id = newTabId();
    setTabs(prev => {
      const nextIndex = (prev[prev.length - 1]?.index ?? 0) + 1;
      return [...prev, { id, customLabel: '', index: nextIndex }];
    });
    setActiveKey(id);
  }, []);

  const removeTab = useCallback((targetKey: string) => {
    Modal.confirm({
      title: '关闭这个会话?',
      content: '关闭后,该会话内的消息草稿、已选机器人、发送进度都会丢失。',
      okText: '关闭',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => {
        setTabs(prev => {
          if (prev.length <= 1) return prev;
          const idx = prev.findIndex(t => t.id === targetKey);
          const next = prev.filter(t => t.id !== targetKey);
          setActiveKey(curKey => {
            if (curKey !== targetKey) return curKey;
            const fallback = next[Math.max(0, idx - 1)] || next[0];
            return fallback?.id || '';
          });
          return next;
        });
      },
    });
  }, []);

  const updateLabel = useCallback((id: string, label: string) => {
    setTabs(prev => {
      const tab = prev.find(t => t.id === id);
      if (!tab || tab.customLabel === label) return prev;
      return prev.map(t => t.id === id ? { ...t, customLabel: label } : t);
    });
  }, []);

  const items = tabs.map(t => ({
    key: t.id,
    label: t.customLabel || `会话 ${t.index}`,
    closable: tabs.length > 1,
    children: <ChatPanel onLabelChange={(label) => updateLabel(t.id, label)} />,
  }));

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <Title level={4}>专属员工聊天</Title>
      <Tabs
        type="editable-card"
        activeKey={activeKey}
        onChange={setActiveKey}
        onEdit={(key, action) => {
          if (action === 'add') addTab();
          else if (action === 'remove') removeTab(key as string);
        }}
        items={items}
        addIcon={<PlusOutlined />}
      />
    </div>
  );
};

export default EmployeeChat;
